import { type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import styled from "styled-components";

import { SrOnly } from "../foundations/srOnly";
import { IconButton, TooltipIconButton } from "../IconButton";
import { MediaFrameActions } from "../MediaFrame";
import { mediaFullscreenButtonRevealCss } from "../MediaFrame/mediaFullscreenButtonReveal";
import { Tooltip } from "../Tooltip";
import { MinusIcon, PlusIcon, RotateCcwIcon } from "../../icons/glyphs";
import { useZoomPanTransform } from "./useZoomPanTransform";

// 배율 표기는 zoomStep 정밀도를 따른다 — toFixed(1) 고정이면 step 0.01에서 scale(1.01)인데
// "1.0x"로 보여 reset 활성 상태와 모순된 표시가 된다. 소수 자릿수는 step에서 유도(1~3 clamp).
function formatZoom(zoom: number, zoomStep: number | undefined) {
  const fraction = String(zoomStep ?? "").split(".")[1];
  const decimals = Math.max(1, Math.min(fraction ? fraction.length : 0, 3));
  return `${zoom.toFixed(decimals)}x`;
}

const BorderOverlay = styled.div<{ $bordered: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 1px solid
    ${({ theme, $bordered }) => ($bordered ? theme.color.border.default : "transparent")};
  border-radius: inherit;
  transition: border-color 0.15s ease;
`;

const Viewport = styled.div<{ $cursor: CSSProperties["cursor"]; $bordered: boolean }>`
  position: relative;
  width: 100%;
  max-height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  /* pan/축소로 콘텐츠가 밀려나면 드러나는 void — 투명이면 뒤(scrim/페이지)가 비쳐 구멍처럼
     보인다. VideoFrame letterbox와 같은 recessed 톤으로 채운다. */
  background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
  cursor: ${({ $cursor }) => $cursor};

  ${mediaFullscreenButtonRevealCss}

  &:hover ${BorderOverlay} {
    border-color: ${({ theme, $bordered }) =>
      $bordered ? theme.color.border.focus : "transparent"};
  }
`;

const Surface = styled.div`
  width: 100%;
  height: 100%;
`;

// [−][배율][+] 그룹. 버튼은 fullscreen 등 이웃 액션과 동일한 surface variant(같은
// fill/border/높이)를 쓰고, 그룹감은 row(0.25rem)보다 좁은 내부 간격으로만 준다 —
// studio viewport의 ghost-pill은 hover-reveal 오버레이 맥락이라 여기선 채택하지 않는다.
const ZoomCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
`;

// 배율 readout이 곧 reset 버튼 — 자릿수 흔들림은 tabular-nums + min-width로 고정.
const ZoomResetButton = styled(TooltipIconButton)`
  min-width: 2.8rem;
  font-variant-numeric: tabular-nums;
`;

export interface ZoomPanWrapperProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onWheel" | "onPointerDown"
> {
  children: ReactNode;
  /** Minimum zoom factor. Default 0.3. */
  minZoom?: number;
  /** Maximum zoom factor. Default 5. */
  maxZoom?: number;
  /** Zoom delta applied per wheel notch. Default 0.3. */
  zoomStep?: number;
  /**
   * Preserve the relative content view when this viewport changes size by rebasing pixel pan.
   * Default false, preserving the historical fixed-pixel transform behavior.
   */
  preserveRelativeViewOnResize?: boolean;
  /**
   * Draw the viewport border + hover affordance. Default true. Set false when the
   * child already provides its own frame (e.g. a bordered `VideoFrame`) so the two
   * don't stack into a double border.
   */
  bordered?: boolean;
  /** Show a floating reset control after zoom/pan changes. Default false. */
  showResetButton?: boolean;
  /** Accessible label for the reset control. Default "Reset view". */
  resetLabel?: string;
  /**
   * Show a [−][배율][+] zoom cluster in the top-right action row. ± steps are
   * viewport-center anchored and disable at the configured zoom bounds; the
   * middle readout (`1.0x`) doubles as the reset button (disabled at identity),
   * so the standalone `showResetButton` control is absorbed while this is on.
   * The buttons are focusable — they double as the keyboard zoom path.
   * Default false.
   */
  showZoomControls?: boolean;
  /** Accessible label for the zoom-in control. Default "Zoom in". */
  zoomInLabel?: string;
  /** Accessible label for the zoom-out control. Default "Zoom out". */
  zoomOutLabel?: string;
  /**
   * Interactive actions (fullscreen 등) rendered in the top-right action row at the
   * viewport (non-transformed) level, so they do not scale or move with zoom/pan.
   * When the reset control is visible it merges into the same row, to the left —
   * the row is right-anchored, so these actions keep a stable position while the
   * transient reset appears and disappears.
   */
  actions?: ReactNode;
}

/**
 * Clipping viewport that lets the user Ctrl+wheel to zoom toward the pointer
 * and Ctrl+drag to pan its `children`. It can opt into resize rebasing so the same
 * content point keeps its relative viewport position across differently sized
 * presentation hosts. A thin presentational shell over {@link useZoomPanTransform}
 * — for viewing media/images. To toggle the gesture
 * on/off, wrap conditionally. Consumers that need to read pointer coordinates
 * (e.g. placing annotation points) or transiently `locked` interaction should
 * use the hook directly instead.
 *
 * a11y: the pointer gestures (Ctrl+wheel/drag) have no WAI-ARIA pattern, so the
 * wrapper adds no interactive ARIA semantics and does not trap focus — the
 * `children` remain fully accessible. `showZoomControls`의 ± 버튼이 포커스 가능한
 * 키보드 줌 경로를 제공한다. TBD: keyboard pan(화살표 키)은 미구현.
 */
export function ZoomPanWrapper({
  children,
  minZoom,
  maxZoom,
  zoomStep,
  preserveRelativeViewOnResize = false,
  bordered = true,
  showResetButton = false,
  resetLabel = "Reset view",
  showZoomControls = false,
  zoomInLabel = "Zoom in",
  zoomOutLabel = "Zoom out",
  actions,
  ...rest
}: ZoomPanWrapperProps) {
  const {
    zoom,
    panX,
    panY,
    interactionCursor,
    transformStyle,
    containerRef,
    handlePointerDown,
    canZoomIn,
    canZoomOut,
    zoomIn,
    zoomOut,
    reset,
  } = useZoomPanTransform({
    minZoom,
    maxZoom,
    zoomStep,
    preserveRelativeViewOnResize,
  });
  const isTransformed = zoom !== 1 || panX !== 0 || panY !== 0;
  // 줌 클러스터가 켜지면 배율 readout이 reset을 흡수한다 — standalone reset은 wheel 전용 경로.
  const showReset = showResetButton && isTransformed && !showZoomControls;
  const zoomText = formatZoom(zoom, zoomStep);

  return (
    <Viewport
      ref={containerRef}
      $cursor={interactionCursor}
      $bordered={bordered}
      onPointerDown={handlePointerDown}
      {...rest}
    >
      <Surface style={transformStyle}>{children}</Surface>
      {/* 액션 row는 transform 되는 Surface 밖(viewport 레벨)에 있어 zoom/pan 을 따라가지 않는다.
          전파 차단(pan 시작 억제 등)은 MediaFrameActions 컨테이너가 담당. Tooltip 은 portal+fixed 라
          Viewport 의 overflow:hidden 에도 잘리지 않는다. */}
      {showReset || showZoomControls || actions ? (
        <MediaFrameActions>
          {showReset ? (
            // describeTrigger=false: 툴팁 내용이 곧 접근성 이름(aria-label)이라 aria-describedby 중복 방지.
            <Tooltip content={resetLabel} placement="bottom" describeTrigger={false}>
              <IconButton size="sm" variant="surface" aria-label={resetLabel} onClick={reset}>
                <RotateCcwIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          {showZoomControls ? (
            <ZoomCluster>
              <TooltipIconButton
                size="sm"
                variant="surface"
                tooltip={{ content: zoomOutLabel, placement: "bottom" }}
                aria-label={zoomOutLabel}
                disabled={!canZoomOut}
                onClick={zoomOut}
              >
                <MinusIcon />
              </TooltipIconButton>
              <ZoomResetButton
                size="sm"
                variant="surface"
                tooltip={{ content: resetLabel, placement: "bottom" }}
                // 보이는 배율 텍스트를 접근성 이름에 포함 — resetLabel만 주면 스크린리더가
                // 현재 배율을 어디서도 들을 수 없다(label-in-name).
                aria-label={`${zoomText}, ${resetLabel}`}
                disabled={!isTransformed}
                onClick={reset}
              >
                {zoomText}
              </ZoomResetButton>
              <TooltipIconButton
                size="sm"
                variant="surface"
                tooltip={{ content: zoomInLabel, placement: "bottom" }}
                aria-label={zoomInLabel}
                disabled={!canZoomIn}
                onClick={zoomIn}
              >
                <PlusIcon />
              </TooltipIconButton>
              {/* ± 활성화 직후엔 포커스가 ±에 있어 readout 이름 변화가 낭독되지 않는다 —
                  결과 배율을 polite 라이브 리전으로 알린다. */}
              <SrOnly role="status">{zoomText}</SrOnly>
            </ZoomCluster>
          ) : null}
          {actions}
        </MediaFrameActions>
      ) : null}
      <BorderOverlay $bordered={bordered} aria-hidden="true" />
    </Viewport>
  );
}

ZoomPanWrapper.displayName = "ZoomPanWrapper";
